import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../App';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { 
  Trophy, Users, Calendar, Loader2, LayoutGrid, List,
  Droplets, Beef, CandyOff, Dumbbell, Footprints, Moon, Award, Flame, Star, Target, Zap
} from 'lucide-react';

// Icon mapping for badges
const iconMap = {
  droplets: Droplets,
  beef: Beef,
  'candy-off': CandyOff,
  dumbbell: Dumbbell,
  footprints: Footprints,
  moon: Moon,
  award: Award,
  flame: Flame,
  star: Star,
  target: Target,
  zap: Zap,
  trophy: Trophy
};

const GroupMilestones = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('timeline'); // 'timeline' or 'grouped'
  const [fistbumpingId, setFistbumpingId] = useState(null);
  const [showingFistbumps, setShowingFistbumps] = useState({});
  const [fistbumpNames, setFistbumpNames] = useState({});

  const fetchMilestones = useCallback(async () => {
    try {
      const res = await apiClient.get(`/group-milestones?view=${view}`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch group milestones:', err);
      toast.error('Failed to load group milestones');
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => {
    fetchMilestones();
  }, [fetchMilestones]);

  const handleFistbump = async (achievementId) => {
    setFistbumpingId(achievementId);
    try {
      const res = await apiClient.post(`/group-milestones/${achievementId}/fistbump`);
      toast.success(res.data.message);
      // Refresh data to update fistbump counts
      fetchMilestones();
    } catch (err) {
      toast.error('Failed to send fist bump');
    } finally {
      setFistbumpingId(null);
    }
  };

  const fetchFistbumpNames = async (achievementId) => {
    if (fistbumpNames[achievementId]) {
      setShowingFistbumps(prev => ({ ...prev, [achievementId]: !prev[achievementId] }));
      return;
    }
    
    try {
      const res = await apiClient.get(`/group-milestones/${achievementId}/fistbumps`);
      setFistbumpNames(prev => ({ ...prev, [achievementId]: res.data }));
      setShowingFistbumps(prev => ({ ...prev, [achievementId]: true }));
    } catch (err) {
      console.error('Failed to fetch fist bump names:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Handle YYYY-MM-DD format (for_date) or ISO datetime (earned_at)
    const date = dateString.includes('T') 
      ? new Date(dateString) 
      : new Date(dateString + 'T12:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Get the display date - prefer for_date (the date it was earned FOR) over earned_at
  const getDisplayDate = (achievement) => {
    return achievement.for_date || achievement.earned_at;
  };

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || Trophy;
    return <IconComponent size={20} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#EFB342] animate-spin" />
      </div>
    );
  }

  const renderAchievementCard = (achievement, showUser = true) => (
    <div 
      key={achievement.id}
      className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-all"
      data-testid={`milestone-${achievement.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Badge Icon */}
        <div className="w-12 h-12 bg-gradient-to-br from-[#EFB342]/20 to-[#EFB342]/5 rounded-xl flex items-center justify-center text-[#EFB342] flex-shrink-0">
          {getIcon(achievement.badge_icon)}
        </div>
        
        <div className="flex-1 min-w-0">
          {/* User Name (if showing) */}
          {showUser && (
            <p className="text-white font-medium text-sm mb-1">{achievement.user_name}</p>
          )}
          
          {/* Badge Name */}
          <p className="text-[#EFB342] font-semibold">{achievement.badge_name}</p>
          
          {/* Date */}
          <p className="text-zinc-500 text-xs mt-1 flex items-center gap-1">
            <Calendar size={12} />
            {formatDate(getDisplayDate(achievement))}
          </p>
        </div>
        
        {/* Fist Bump Button */}
        <div className="flex flex-col items-end gap-1">
          <Button
            size="sm"
            variant={achievement.user_fistbumped ? "default" : "outline"}
            onClick={() => handleFistbump(achievement.id)}
            disabled={fistbumpingId === achievement.id}
            className={`text-xs px-3 py-1 h-8 ${
              achievement.user_fistbumped 
                ? 'bg-[#EFB342] text-black hover:bg-[#D99F30]' 
                : 'border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-[#EFB342]'
            }`}
            data-testid={`fistbump-${achievement.id}`}
          >
            {fistbumpingId === achievement.id ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <>👊 {achievement.fistbump_count > 0 ? achievement.fistbump_count : ''}</>
            )}
          </Button>
          
          {/* Show who fist bumped */}
          {achievement.fistbump_count > 0 && (
            <button
              onClick={() => fetchFistbumpNames(achievement.id)}
              className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              {showingFistbumps[achievement.id] ? 'Hide' : 'Who?'}
            </button>
          )}
        </div>
      </div>
      
      {/* Fist bump names dropdown */}
      {showingFistbumps[achievement.id] && fistbumpNames[achievement.id] && (
        <div className="mt-3 pt-3 border-t border-zinc-800">
          <p className="text-xs text-zinc-500 mb-1">Celebrated by:</p>
          <div className="flex flex-wrap gap-1">
            {fistbumpNames[achievement.id].map((fb) => (
              <span key={fb.id} className="text-xs bg-zinc-800 text-zinc-300 px-2 py-1 rounded-full">
                {fb.user_name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header with Stats and Toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#EFB342]/20 rounded-xl flex items-center justify-center">
            <Trophy className="text-[#EFB342]" size={20} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">{data?.month || 'This Month'}</h2>
            <p className="text-sm text-zinc-400">
              {data?.total_count || 0} milestone{data?.total_count !== 1 ? 's' : ''} achieved!
            </p>
          </div>
        </div>
        
        {/* View Toggle */}
        <div className="flex items-center gap-1 bg-zinc-900 rounded-lg p-1">
          <button
            onClick={() => setView('timeline')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-all ${
              view === 'timeline' 
                ? 'bg-[#EFB342] text-black font-medium' 
                : 'text-zinc-400 hover:text-white'
            }`}
          >
            <List size={16} />
            Timeline
          </button>
          <button
            onClick={() => setView('grouped')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-all ${
              view === 'grouped' 
                ? 'bg-[#EFB342] text-black font-medium' 
                : 'text-zinc-400 hover:text-white'
            }`}
          >
            <LayoutGrid size={16} />
            By User
          </button>
        </div>
      </div>

      {/* Content */}
      {data?.total_count === 0 ? (
        <div className="text-center py-12 bg-zinc-900/30 rounded-2xl border border-zinc-800">
          <Trophy className="mx-auto text-zinc-600 mb-4" size={48} />
          <p className="text-zinc-400 text-lg mb-2">No milestones yet this month</p>
          <p className="text-zinc-500 text-sm">Keep crushing your goals - milestones will appear here!</p>
        </div>
      ) : view === 'timeline' ? (
        /* Timeline View */
        <div className="space-y-3">
          {data?.achievements?.map((achievement) => renderAchievementCard(achievement, true))}
        </div>
      ) : (
        /* Grouped by User View */
        <div className="space-y-6">
          {data?.users?.map((userData) => (
            <div key={userData.user_id} className="bg-zinc-900/30 border border-zinc-800 rounded-2xl p-4">
              <div className="flex items-center gap-3 mb-4 pb-3 border-b border-zinc-800">
                <div className="w-10 h-10 bg-gradient-to-br from-[#EFB342] to-[#D99F30] rounded-full flex items-center justify-center text-black font-bold text-lg">
                  {userData.user_name?.charAt(0).toUpperCase() || '?'}
                </div>
                <div>
                  <p className="text-white font-semibold">{userData.user_name}</p>
                  <p className="text-zinc-500 text-xs">{userData.achievements.length} milestone{userData.achievements.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <div className="space-y-3">
                {userData.achievements.map((achievement) => renderAchievementCard(achievement, false))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GroupMilestones;
